import React, { useState, useEffect, useRef } from 'react';
import { IGetReviewPageParams, IReviewPage, ReviewAssetsFilter } from 'mod/review/interface';
import Message from '@base/lib/Message';
import { IScrollToAnchorConfig, scrollToAnchor } from 'base/utils/scrollTo';
import { getReviewPage } from 'mod/review/service';

type IProps = {
    variantId: string;
    from: IScrollToAnchorConfig['from'];
    initialReviewPage?: IReviewPage;
};

export type IReviewPageInstance = {
    anchorRef: React.Ref<HTMLDivElement>;
    reviewPage?: IReviewPage;
    withPictures: ReviewAssetsFilter;
    page: number;
    loading: boolean;
    onPageChange: (page: number) => void;
    onWithPicturesChange: (filter: ReviewAssetsFilter) => void;
    scrollToTop: () => void;
    resetReviewPage: () => void;
    afterCount: number;
};

function sliceReviewPage(reviewPage: IReviewPage): IReviewPage {
    return {
        ...reviewPage,
        page: 1,
        pageSize: 10,
        totalPage: Math.ceil(reviewPage.totalNum / 10),
        list: reviewPage.list.slice(0, 10)
    }
}

export function useReviewPage(props: IProps): IReviewPageInstance {
    const {
        variantId,
        from,
        initialReviewPage
    } = props;

    const anchorRef = useRef<HTMLDivElement>(null);
    const loadedRef = useRef<boolean>(false);
    const [reviewPage, setReviewPage] = useState<IReviewPage | undefined>(initialReviewPage ? sliceReviewPage(initialReviewPage) : initialReviewPage);
    const [withPictures, setWithPictures] = useState<ReviewAssetsFilter>(0);
    const [page, setPage] = useState<number>(1);
    const [loading, setLoading] = useState<boolean>(false);
    const [afterCount, setAfterCount] = useState<number>(0) // 页面请求完后修改 （油猴脚本监听修改）

    const scrollToTop = () => {
        const anchor = anchorRef.current;
        if (!anchor) {
            return;
        }
        scrollToAnchor({
            selector: anchor,
            behavior: 'instant',
            from,
        });
    };

    const fetchReviewPage = async (params: Partial<IGetReviewPageParams> = {}) => {
        params.page && setPage(params.page)
        typeof params.withPictures !== 'undefined' && setWithPictures(params.withPictures);
        setLoading(true);
        try {
            const res = await getReviewPage({
                productVariantId: variantId || '',
                page,
                pageSize: 10,
                withPictures: withPictures,
                ...params
            });
            setReviewPage(res);
            setAfterCount(afterCount + 1);
        } catch (e) {
            Message.error(e.message);
        }
        setLoading(false);
    };

    const onPageChange = (page: number) => {
        scrollToTop();
        fetchReviewPage({ page })
    }

    const onWithPicturesChange = (value: ReviewAssetsFilter) => {
        scrollToTop();
        setWithPictures(value);
        fetchReviewPage({ page: 1, withPictures: value });
    };

    const resetReviewPage = () => {
        fetchReviewPage({
            productVariantId: variantId,
            page: 1,
            withPictures: 0
        });
    }

    useEffect(() => {
        if (loadedRef.current) {
            resetReviewPage();
        } else {
            loadedRef.current = true;
            if (!initialReviewPage) {
                resetReviewPage();
            }
        }
    }, [variantId]);

    return {
        anchorRef,
        reviewPage,
        withPictures,
        page,
        loading,
        onPageChange,
        onWithPicturesChange,
        scrollToTop,
        resetReviewPage,
        afterCount,
    };
}